import { makeApiRequest } from '@/src/lib/api-request'
import { formatDate } from '@/src/lib/utils'
import { useToken } from '@/src/store/token-store'
import { useUser } from '@/src/store/user-store'
import { useInfiniteQuery } from '@tanstack/react-query'

interface CreatorBreakdown {
  UniqueWatchTime: string
  watch_time: number
  duration: number
  earning: string
  lykCoin: string
  watchCount: number
  likeCount: number
  commentCount: number
  eventId: string
  eventTitle: string
  eventContent: string
  thumbnailImage: string
  channelName: string
  channelShareName: string
  encryptVideoUrl: string
  createdAt: string
  type: string
  status: string
}

interface CreatorBreakdownResponse {
  creatorBreakdown: CreatorBreakdown[]
  offset: number
}

export const useCreatorBreakdown = ({
  limit,
  offset,
  sreach_text,
  startDate,
  endDate,
  sortBy,
}: {
  limit: number
  offset: number
  sreach_text?: string
  startDate?: Date
  endDate?: Date
  sortBy: string
}) => {
  const { _hasHydrated } = useUser()
  const { accessToken } = useToken()
  return useInfiniteQuery({
    initialPageParam: { offset: 0 },
    queryKey: [
      'breakdown',
      'creator',
      limit,
      offset,
      sreach_text,
      startDate,
      endDate,
      sortBy,
    ],
    queryFn: async (): Promise<
      CreatorBreakdownResponse & { pageCount: number }
    > => {
      const newOffset = offset || 0
      const dataProps =
        startDate && endDate
          ? {
              limit: limit,
              offset: newOffset,
              sreach_text: sreach_text,
              startDate: formatDate(startDate),
              endDate: formatDate(endDate),
              sortBy: sortBy,
            }
          : {
              limit: limit,
              offset: newOffset,
              sreach_text: sreach_text,
              sortBy: sortBy,
            }

      const response = await makeApiRequest({
        method: 'POST',
        url: '/Earning/getAllEarningDetails_Creator',
        isAuthenticated: true,
        data: dataProps,
        fallbackError: 'Error while fetching creator breakdown',
        isV3Api: true,
      })
      const creatorBreakdown: CreatorBreakdown[] = response.response
        ? response.response
        : []
      const pageCount: number = response.pageCount || 1

      return { creatorBreakdown, offset: newOffset, pageCount }
    },
    getNextPageParam: (lastPage) => {
      const newOffset = lastPage.offset + limit
      return lastPage.creatorBreakdown.length === limit
        ? { offset: newOffset }
        : undefined
    },
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    enabled: !!accessToken && _hasHydrated,
  })
}
